// Workbench centre-pane editor for the Module 4 "Terrain + LOS" section.
// Lists the scenario's attached terrain + obstacle packs, lets the user
// attach a different DEM/pack, and renders a live LOS preview line
// between TX / RX markers. On debounce, it calls
// POST /api/sim/scenarios/{id}/los and colours the line red (blocked)
// or green (clear).
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import { Input } from "@/components/ui/input";
import SpinnerButton from "@/components/SpinnerButton";
import EmptyState from "@/components/EmptyState";
import {
    terrain as terrainApi,
    obstacles as obstaclesApi,
    scenarioTerrain,
} from "@/api/endpoints";

const MapEditor = lazy(() => import("@/components/terrain/MapEditor"));

const NONE = "__none__";

// Phase 18 Ask 4.2 — heatmap resolution multiplier lives in
// ./heatmapResolution so it can be unit-tested without dragging in
// react-router (jest-resolves poorly under our craco config).
import { resolveHeatmapGrid } from "./heatmapResolution";
export { resolveHeatmapGrid };

function useDebounced(fn, ms) {
    const ref = useRef(null);
    return useCallback(
        (...args) => {
            if (ref.current) clearTimeout(ref.current);
            ref.current = setTimeout(() => fn(...args), ms);
        },
        [fn, ms],
    );
}

export default function WorkbenchTerrainPane({ scenario, readOnly = false, onScenarioChanged }) {
    const scenarioId = scenario?.id;
    const [terrainList, setTerrainList] = useState([]);
    const [obstacleList, setObstacleList] = useState([]);
    const [terrainDoc, setTerrainDoc] = useState(null);
    const [obstacleDocs, setObstacleDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [attaching, setAttaching] = useState(false);
    const [tx, setTx] = useState({ lon: "", lat: "", agl_m: 20 });
    const [rx, setRx] = useState({ lon: "", lat: "", agl_m: 20 });
    const [losResult, setLosResult] = useState(null);
    const [losPending, setLosPending] = useState(false);
    const [losError, setLosError] = useState(null);
    // Module 6 — RF visibility heatmap toggle.
    const [heatmapOn, setHeatmapOn] = useState(false);
    const [heatmapPending, setHeatmapPending] = useState(false);
    const [heatmapData, setHeatmapData] = useState(null);
    // Phase 18 Ask 4.2 — heatmap resolution multiplier. The base grid
    // is 16×12; the slider lets the operator dial it up to 4× for
    // tighter denial corridors. Higher resolution = more LOS rays =
    // slower but more accurate.
    const [heatmapResolution, setHeatmapResolution] = useState(1);

    // Load reference lists once.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [t, o] = await Promise.all([
                    terrainApi.list({ scope: "all" }),
                    obstaclesApi.list({ scope: "all" }),
                ]);
                if (!alive) return;
                setTerrainList(Array.isArray(t) ? t : []);
                setObstacleList(Array.isArray(o) ? o : []);
            } catch (e) {
                toast.error(formatApiError(e, "Load failed"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Hydrate attached terrain + obstacle docs from the scenario refs.
    useEffect(() => {
        let alive = true;
        if (!scenario?.terrain_ref) {
            setTerrainDoc(null);
        } else {
            terrainApi
                .get(scenario.terrain_ref)
                .then((doc) => alive && setTerrainDoc(doc))
                .catch(() => alive && setTerrainDoc(null));
        }
        const refs = scenario?.obstacle_refs || [];
        if (refs.length === 0) {
            setObstacleDocs([]);
        } else {
            Promise.all(refs.map((id) => obstaclesApi.get(id).catch(() => null))).then(
                (docs) => alive && setObstacleDocs(docs.filter(Boolean)),
            );
        }
        return () => { alive = false; };
    }, [scenario?.terrain_ref, scenario?.obstacle_refs]);

    // Default TX/RX to the terrain bbox centre offsets once a terrain is attached.
    useEffect(() => {
        if (!terrainDoc?.bbox) return;
        const b = terrainDoc.bbox;
        const cx = (b.lon_min + b.lon_max) / 2;
        const cy = (b.lat_min + b.lat_max) / 2;
        const dx = (b.lon_max - b.lon_min) * 0.35;
        const dy = (b.lat_max - b.lat_min) * 0.0;
        setTx((t) => ({ ...t, lon: t.lon || (cx - dx), lat: t.lat || (cy + dy) }));
        setRx((r) => ({ ...r, lon: r.lon || (cx + dx), lat: r.lat || (cy + dy) }));
    }, [terrainDoc?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const runLos = useCallback(async () => {
        if (!scenarioId || !terrainDoc) return;
        if (tx.lon === "" || tx.lat === "" || rx.lon === "" || rx.lat === "") return;
        setLosPending(true);
        setLosError(null);
        try {
            const body = {
                tx: { lon: +tx.lon, lat: +tx.lat, agl_m: +tx.agl_m },
                rx: { lon: +rx.lon, lat: +rx.lat, agl_m: +rx.agl_m },
                include_profile: false,
            };
            const res = await scenarioTerrain.los(scenarioId, body);
            setLosResult(res);
        } catch (e) {
            setLosError(formatApiError(e, "LOS check failed"));
            setLosResult(null);
        } finally {
            setLosPending(false);
        }
    }, [scenarioId, terrainDoc, tx, rx]);

    const runLosDebounced = useDebounced(runLos, 350);
    useEffect(() => {
        if (terrainDoc) runLosDebounced();
    }, [tx.lon, tx.lat, tx.agl_m, rx.lon, rx.lat, rx.agl_m, terrainDoc, runLosDebounced]);

    const attachTerrain = async (val) => {
        if (readOnly) return;
        setAttaching(true);
        try {
            if (val === NONE) {
                await scenarioTerrain.detach(scenarioId);
                toast.success("Terrain detached");
            } else {
                await scenarioTerrain.attach(scenarioId, { terrain_id: val });
                toast.success("Terrain attached");
            }
            onScenarioChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Attach failed"));
        } finally {
            setAttaching(false);
        }
    };

    const attachObstacle = async (val) => {
        if (readOnly || val === NONE) return;
        setAttaching(true);
        try {
            await scenarioTerrain.attachObstacle(scenarioId, val);
            toast.success("Obstacle attached");
            onScenarioChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Attach failed"));
        } finally {
            setAttaching(false);
        }
    };

    const detachObstacle = async (obstacleId) => {
        if (readOnly) return;
        setAttaching(true);
        try {
            await scenarioTerrain.detachObstacle(scenarioId, obstacleId);
            toast.success("Obstacle detached");
            onScenarioChanged?.();
        } catch (e) {
            toast.error(formatApiError(e, "Detach failed"));
        } finally {
            setAttaching(false);
        }
    };

    // Phase 18 Ask 4.2 — pure helper exposing the resolution → grid
    // mapping so it can be unit-tested without rendering the pane.
    // Base grid 16×12; multiplier ∈ {1,2,3,4} scales both axes.
    const resolvedGrid = resolveHeatmapGrid(heatmapResolution);

    // Module 6 — RF visibility heatmap fetch.
    const fetchHeatmap = useCallback(async () => {
        if (!scenarioId || !terrainDoc) return;
        setHeatmapPending(true);
        try {
            const b = terrainDoc.bbox;
            const body = {
                tx: { lon: +tx.lon, lat: +tx.lat, agl_m: +tx.agl_m },
                rx_grid: {
                    bbox: [b.lon_min, b.lat_min, b.lon_max, b.lat_max],
                    nx: resolvedGrid.nx, ny: resolvedGrid.ny,
                    agl_m: Number(rx.agl_m) || 20,
                },
                sample_spacing_m: 20.0,
            };
            const res = await scenarioTerrain.losGrid(scenarioId, body);
            // Server returns a base64-encoded byte array (1 = clear,
            // 0 = blocked). Decode to a 2D array shaped for MapEditor.
            const bytes = Uint8Array.from(atob(res.visibility_b64 || ""), (c) => c.charCodeAt(0));
            const nx = res.nx, ny = res.ny;
            const rows = [];
            for (let j = 0; j < ny; j++) {
                rows.push(Array.from(bytes.slice(j * nx, (j + 1) * nx)));
            }
            setHeatmapData({
                bbox: { lon_min: b.lon_min, lat_min: b.lat_min,
                        lon_max: b.lon_max, lat_max: b.lat_max },
                nx, ny, visibility: rows,
            });
        } catch (e) {
            toast.error(formatApiError(e, "Heatmap failed"));
            setHeatmapData(null);
        } finally {
            setHeatmapPending(false);
        }
    }, [scenarioId, terrainDoc, tx, rx.agl_m, resolvedGrid.nx, resolvedGrid.ny]);

    useEffect(() => {
        if (heatmapOn && terrainDoc && tx.lon !== "" && tx.lat !== "") {
            fetchHeatmap();
        }
        if (!heatmapOn) setHeatmapData(null);
    }, [heatmapOn, fetchHeatmap, terrainDoc, tx.lon, tx.lat, heatmapResolution]);


    const losLine = useMemo(() => {
        if (!losResult || tx.lon === "" || rx.lon === "") return null;
        return {
            from: [+tx.lat, +tx.lon],
            to: [+rx.lat, +rx.lon],
            blocked: losResult.clear === false,
        };
    }, [losResult, tx, rx]);

    const centre = useMemo(() => {
        if (!terrainDoc?.bbox) return [51.51, 0.0];
        return [
            (terrainDoc.bbox.lat_min + terrainDoc.bbox.lat_max) / 2,
            (terrainDoc.bbox.lon_min + terrainDoc.bbox.lon_max) / 2,
        ];
    }, [terrainDoc]);

    if (loading) {
        return <div className="text-xs text-ux-text-3">Loading terrain & obstacles…</div>;
    }

    return (
        <div className="space-y-4" data-testid="workbench-terrain-pane">
            <div
                className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 flex flex-wrap items-end gap-4"
                data-testid="workbench-terrain-attach"
            >
                <label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3 space-y-1">
                    Terrain DEM
                    <Select
                        value={terrainDoc?.id || NONE}
                        onValueChange={attachTerrain}
                        disabled={attaching || readOnly}
                    >
                        <SelectTrigger
                            className="w-[240px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="workbench-terrain-select"
                        >
                            <SelectValue placeholder="— none —" />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            <SelectItem value={NONE} className="text-[12px] font-mono">— none —</SelectItem>
                            {terrainList.map((t) => (
                                <SelectItem
                                    key={t.id}
                                    value={t.id}
                                    className="text-[12px] font-mono"
                                    data-testid={`workbench-terrain-option-${t.id}`}
                                >
                                    {t.name} {t.is_preset ? "· preset" : ""}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </label>
                <label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3 space-y-1">
                    Attach obstacle pack
                    <Select
                        value={NONE}
                        onValueChange={attachObstacle}
                        disabled={attaching || readOnly}
                    >
                        <SelectTrigger
                            className="w-[240px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="workbench-obstacle-select"
                        >
                            <SelectValue placeholder="— choose —" />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            <SelectItem value={NONE} className="text-[12px] font-mono">— choose —</SelectItem>
                            {obstacleList.map((o) => (
                                <SelectItem
                                    key={o.id}
                                    value={o.id}
                                    className="text-[12px] font-mono"
                                >
                                    {o.name} · {o.feature_count} feat.
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </label>
                <Link
                    to="/plan/terrain"
                    className="ml-auto text-xs text-ux-accent underline decoration-dotted"
                >
                    Open terrain library <ExternalLink className="inline w-3 h-3" />
                </Link>
            </div>

            {obstacleDocs.length > 0 ? (
                <div
                    className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 flex flex-wrap gap-2"
                    data-testid="workbench-obstacle-attached"
                >
                    {obstacleDocs.map((o) => (
                        <div
                            key={o.id}
                            className="flex items-center gap-2 text-[11px] font-mono border border-ux-border-1 rounded-full px-2 py-1"
                        >
                            <span>{o.name}</span>
                            <button
                                type="button"
                                onClick={() => detachObstacle(o.id)}
                                className="text-ux-text-3 hover:text-ux-danger"
                                data-testid={`workbench-obstacle-detach-${o.id}`}
                                disabled={readOnly}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            ) : null}

            {!terrainDoc ? (
                <EmptyState
                    title="No terrain attached"
                    body="Pick a DEM above — Module 4 will use it for RF line-of-sight and obstacle geometry validation."
                />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div
                        className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 space-y-3"
                        data-testid="workbench-los-controls"
                    >
                        <div className="flex items-center justify-between">
                            <h4 className="font-mono text-xs uppercase tracking-wider text-ux-text-3">
                                LOS preview
                            </h4>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={runLos}
                                disabled={losPending}
                                data-testid="workbench-los-refresh"
                            >
                                <RefreshCcw className="h-3.5 w-3.5 mr-1" />
                                Refresh
                            </Button>
                            <label className="flex items-center gap-1 text-[10px] font-mono uppercase text-ux-text-3 ml-2"
                                   data-testid="workbench-los-heatmap-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={heatmapOn}
                                    onChange={(e) => setHeatmapOn(e.target.checked)}
                                    data-testid="workbench-los-heatmap-toggle"
                                    className="accent-ux-accent"
                                />
                                RF heatmap
                                {heatmapPending ? " …" : null}
                            </label>
                            {/* Phase 18 Ask 4.2 — resolution slider. */}
                            {heatmapOn && (
                                <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-ux-text-3"
                                       data-testid="workbench-heatmap-resolution-label">
                                    res
                                    <input
                                        type="range"
                                        min="1"
                                        max="4"
                                        step="1"
                                        value={heatmapResolution}
                                        onChange={(e) => setHeatmapResolution(Number(e.target.value) || 1)}
                                        data-testid="workbench-heatmap-resolution-slider"
                                        className="w-20 accent-ux-accent"
                                        aria-label="RF heatmap resolution multiplier (1× to 4×)"
                                    />
                                    <span data-testid="workbench-heatmap-resolution-readout"
                                          className="tabular-nums">
                                        {heatmapResolution}× ({resolvedGrid.nx}×{resolvedGrid.ny})
                                    </span>
                                </label>
                            )}
                        </div>
                        {["tx", "rx"].map((role) => {
                            const state = role === "tx" ? tx : rx;
                            const set = role === "tx" ? setTx : setRx;
                            const label = role === "tx" ? "Transmitter" : "Receiver";
                            return (
                                <div key={role} className="grid grid-cols-3 gap-2">
                                    <div className="col-span-3 text-[11px] font-mono uppercase tracking-wider text-ux-text-3">
                                        {label}
                                    </div>
                                    <label className="text-[10px] font-mono uppercase text-ux-text-3">
                                        lon
                                        <Input
                                            value={state.lon}
                                            onChange={(e) => set({ ...state, lon: e.target.value })}
                                            className="h-8 text-[12px] font-mono"
                                            data-testid={`workbench-los-${role}-lon`}
                                        />
                                    </label>
                                    <label className="text-[10px] font-mono uppercase text-ux-text-3">
                                        lat
                                        <Input
                                            value={state.lat}
                                            onChange={(e) => set({ ...state, lat: e.target.value })}
                                            className="h-8 text-[12px] font-mono"
                                            data-testid={`workbench-los-${role}-lat`}
                                        />
                                    </label>
                                    <label className="text-[10px] font-mono uppercase text-ux-text-3">
                                        agl (m)
                                        <Input
                                            value={state.agl_m}
                                            onChange={(e) => set({ ...state, agl_m: e.target.value })}
                                            className="h-8 text-[12px] font-mono"
                                            data-testid={`workbench-los-${role}-agl`}
                                        />
                                    </label>
                                </div>
                            );
                        })}

                        <div className="text-xs font-mono text-ux-text-3 mt-2">
                            {losError ? (
                                <span
                                    className="text-ux-danger"
                                    data-testid="workbench-los-error"
                                >
                                    {losError}
                                </span>
                            ) : losResult ? (
                                <span data-testid="workbench-los-result">
                                    {losResult.clear ? (
                                        <span className="text-ux-success">✓ clear</span>
                                    ) : (
                                        <span className="text-ux-danger">✕ blocked</span>
                                    )}{" "}
                                    · first-obstruction{" "}
                                    {losResult.first_obstruction_m != null
                                        ? `${losResult.first_obstruction_m.toFixed(1)} m`
                                        : "—"}{" "}
                                    · clearance{" "}
                                    {losResult.clearance_m != null
                                        ? `${losResult.clearance_m.toFixed(1)} m`
                                        : "—"}
                                </span>
                            ) : losPending ? (
                                <span>Computing…</span>
                            ) : (
                                <span>Set TX/RX lat/lon to preview</span>
                            )}
                        </div>
                    </div>

                    <Suspense
                        fallback={
                            <div
                                data-testid="workbench-terrain-map-loading"
                                className="h-[420px] rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-500"
                            >
                                Loading map…
                            </div>
                        }
                    >
                        <MapEditor
                            centre={centre}
                            zoom={12}
                            terrainTile={terrainDoc}
                            obstacles={obstacleDocs}
                            losLine={losLine}
                            losHeatmap={heatmapData}
                            heightPx={420}
                            testId="workbench-terrain-map"
                        />
                    </Suspense>
                </div>
            )}
        </div>
    );
}
